import { WarningCircle } from '@phosphor-icons/react';
import { useIntl } from 'react-intl';
import { cn } from '@/utils/cn';
import { Button } from './Button';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ErrorStateProps {
  /** Error title. Defaults to i18n "Ocorreu um erro" */
  title?: string;
  /** Error message. Defaults to i18n "Nao foi possivel carregar os dados" */
  message?: string;
  /** Retry callback -- when provided, renders a "Tentar novamente" button */
  onRetry?: () => void;
  className?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Error state placeholder for content areas.
 *
 * Renders a centered block with error icon, title, message, and optional
 * retry button. This component appears BELOW the PageHeader -- the page
 * header should remain visible even during errors.
 */
export function ErrorState({
  title,
  message,
  onRetry,
  className,
}: ErrorStateProps) {
  const intl = useIntl();

  const displayTitle =
    title ??
    intl.formatMessage({
      id: 'common.error.title',
      defaultMessage: 'Ocorreu um erro',
    });

  const displayMessage =
    message ??
    intl.formatMessage({
      id: 'common.error.message',
      defaultMessage: 'Nao foi possivel carregar os dados',
    });

  const retryLabel = intl.formatMessage({
    id: 'common.error.retry',
    defaultMessage: 'Tentar novamente',
  });

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center py-16 px-6 text-center',
        className,
      )}
      role="alert"
    >
      <WarningCircle
        size={48}
        weight="regular"
        className="text-error mb-4"
        aria-hidden="true"
      />
      <h3 className="text-base font-semibold text-text-primary">
        {displayTitle}
      </h3>
      <p className="mt-1.5 text-sm text-text-tertiary max-w-sm">
        {displayMessage}
      </p>
      {onRetry && (
        <div className="mt-6">
          <Button variant="secondary" onClick={onRetry}>
            {retryLabel}
          </Button>
        </div>
      )}
    </div>
  );
}

export default ErrorState;
